/**
 * Turbo Stream Edit Modal Handler
 * ================================
 *
 * Centralized handler for edit modal form submissions with Turbo Stream support.
 * Replaces page reloads with seamless Turbo Stream updates.
 *
 * With no network, a form the page lets be saved offline (one inside an
 * offline_intent_controller.js element) is queued on the device instead and
 * submit resolves { success: true, queued: true }; no stream comes back, so
 * the caller closes its own editor.
 *
 * Usage:
 *   TurboEditModal.submit(formElement, {
 *     entityType: 'quote',
 *     entityId: 123,
 *     url: '/quote/update/123',
 *     onSuccess: () => { ... }
 *   });
 */

(function() {
    'use strict';

    const TurboEditModal = {
        /**
         * Submit form data and handle Turbo Stream response
         */
        async submit(form, options = {}) {
            const {
                entityType,
                entityId,
                status,
                url,
                modalId,
                onSuccess,
                onError
            } = options;

            // Every quote edit surface — the detail page's inline description,
            // price, notes and line-item editors and the list edit modal —
            // funnels through here, so the accepted-quote confirmation sits at
            // this seam rather than being repeated at each call site. It passes
            // straight through for every other entity and status, and callers
            // that omit `status` let it resolve from the page's #quoteData.
            // See static/js/accepted-quote-confirm.js.
            if (window.AcceptedQuoteEditConfirm) {
                const proceed = await window.AcceptedQuoteEditConfirm.gate(
                    form, { entityType, entityId, status }
                );
                // Cancelled, not failed: no toast, no onError, editor left open.
                if (!proceed) return { success: false, cancelled: true };
            }

            // A description photo still uploading is not in the markup yet:
            // the editor inserts it when the upload answers. Read the form now
            // and the save posts the description without it, the photo sits in
            // the gallery alone, and the save's re-render tears down the editor
            // the photo was due to land in. Awaited only when one is pending,
            // so every other save still reads its form at once.
            const uploads = this.descriptionUploads(form);
            if (uploads) await uploads;

            const formData = new FormData(form);

            // No network: a form the page lets be saved offline is queued on
            // the device instead (offline_intent_controller.js), and the
            // caller treats it as saved.
            const queued = { success: true, queued: true };
            if (!navigator.onLine && await this.queueOffline(form)) {
                if (modalId) this.closeModal(modalId);
                return queued;
            }

            try {
                let response;
                try {
                    response = await fetch(url, {
                        method: 'POST',
                        body: formData,
                        credentials: 'same-origin',
                        headers: {
                            'Accept': 'text/vnd.turbo-stream.html, application/json',
                            'X-Requested-With': 'XMLHttpRequest'
                        }
                    });
                } catch (networkError) {
                    // The request never reached the server (onLine can say
                    // true with no route out): same offline path.
                    if (await this.queueOffline(form)) {
                        if (modalId) this.closeModal(modalId);
                        return queued;
                    }
                    throw networkError;
                }

                // Refused as stale: tab-session.js has announced the conflict and
                // the conflict sheet takes over (reload, now vs yours), so the
                // editor closes without an error toast of its own.
                if (response.status === 409) {
                    if (modalId) this.closeModal(modalId);
                    return { success: false, conflict: true };
                }

                // Check for HTTP errors first
                if (!response.ok) {
                    const errorText = await response.text();
                    // Try to parse as JSON to get specific error message
                    try {
                        const errorJson = JSON.parse(errorText);
                        if (errorJson.error) {
                            this.showNotification(errorJson.error, 'error');
                            if (onError) onError(errorJson);
                            return { success: false, error: errorJson.error };
                        }
                    } catch (_parseError) {
                        // Not JSON, fall through to generic error
                    }
                    throw new Error(`HTTP ${response.status}: ${errorText.substring(0, 100)}`);
                }

                const contentType = response.headers.get('Content-Type') || '';

                // Handle Turbo Stream response
                if (contentType.includes('text/vnd.turbo-stream.html')) {
                    const turboStreamHtml = await response.text();
                    const isFormError = response.headers.get('X-Form-Error') === '1';

                    // Form-error stream: render into the modal's error region,
                    // keep modal open, no success toast.
                    if (isFormError) {
                        this.processTurboStream(turboStreamHtml);
                        if (onError) onError({ formError: true });
                        return { success: false, formError: true };
                    }

                    // Close modal first
                    if (modalId) {
                        this.closeModal(modalId);
                    }

                    // Process Turbo Stream
                    this.processTurboStream(turboStreamHtml);

                    // Show success notification
                    this.showNotification(`${this.capitalize(entityType)} updated successfully!`, 'success');

                    if (onSuccess) onSuccess();
                    return { success: true };
                }

                // Handle JSON response (validation errors, etc.)
                if (contentType.includes('application/json')) {
                    const jsonResponse = await response.json();

                    if (jsonResponse.success === false) {
                        this.showNotification('Error: ' + (jsonResponse.error || 'Update failed'), 'error');
                        if (onError) onError(jsonResponse);
                        return { success: false, error: jsonResponse.error };
                    }

                    // Legacy JSON success (shouldn't happen with new routes)
                    if (modalId) {
                        this.closeModal(modalId);
                    }
                    this.showNotification(jsonResponse.message || `${this.capitalize(entityType)} updated successfully!`, 'success');

                    // Fallback to page reload for legacy responses
                    setTimeout(() => location.reload(), 1200);

                    if (onSuccess) onSuccess();
                    return { success: true };
                }

                // Unknown response type
                throw new Error('Unexpected response type: ' + contentType);

            } catch (error) {
                console.error(`Error updating ${entityType}:`, error);
                this.showNotification(`Error updating ${entityType}. Please try again.`, 'error');
                if (onError) onError(error);
                return { success: false, error: error.message };
            }
        },

        /**
         * A promise of the photo uploads still running in the form's
         * description editors (rich_description_controller.js), or null when
         * none is.
         */
        descriptionUploads(form) {
            const uploading = Array.from(form.querySelectorAll('.rich-description'))
                .filter((field) => field.chippieUploading && field.chippieUploading());
            if (!uploading.length) return null;
            return Promise.all(uploading.map((field) => field.chippieUploadsSettled()));
        },

        /**
         * Offer a save that cannot reach the server to the page's offline
         * queue: 'chippie:offline-submit' on the form, which
         * offline_intent_controller.js answers with a promise on
         * detail.queued. Resolves true when the save is on the device, false
         * when the form is not one that may be saved offline.
         */
        async queueOffline(form) {
            const event = new CustomEvent('chippie:offline-submit', { bubbles: true, detail: {} });
            form.dispatchEvent(event);
            return event.detail.queued ? await event.detail.queued : false;
        },

        /**
         * Process Turbo Stream HTML and apply to DOM
         */
        processTurboStream(html) {
            // Use Turbo's built-in method for reliable stream processing
            if (typeof Turbo !== 'undefined' && Turbo.renderStreamMessage) {
                Turbo.renderStreamMessage(html);
            } else {
                // Fallback for environments where Turbo isn't available
                const template = document.createElement('template');
                template.innerHTML = html;
                const streamElements = template.content.querySelectorAll('turbo-stream');
                streamElements.forEach(stream => {
                    document.body.appendChild(stream);
                });
            }
        },

        /**
         * Close Bootstrap modal
         */
        closeModal(modalId) {
            const modalElement = document.getElementById(modalId);
            if (modalElement) {
                const modal = bootstrap.Modal.getInstance(modalElement);
                if (modal) {
                    modal.hide();
                }
            }
        },

        /**
         * Show notification using the app's notification system
         */
        showNotification(message, type) {
            if (typeof window.showNotification === 'function') {
                window.showNotification(message, type);
            } else {
                console.log(`[${type}] ${message}`);
            }
        },

        /**
         * Capitalize first letter
         */
        capitalize(str) {
            return str.charAt(0).toUpperCase() + str.slice(1);
        },

        /**
         * Initialize form handler for an edit modal
         * This is a convenience method that sets up the submit event listener
         */
        initForm(formId, options = {}) {
            const form = document.getElementById(formId);
            if (!form) return;

            // Prevent duplicate initialization
            if (form.dataset.turboEditInitialized === 'true') return;
            form.dataset.turboEditInitialized = 'true';

            form.addEventListener('submit', async (e) => {
                e.preventDefault();

                const entityId = form.querySelector('[name$="_id"]')?.value ||
                                 form.querySelector('[name="quote_id"]')?.value ||
                                 form.querySelector('[name="invoice_id"]')?.value ||
                                 form.querySelector('[name="client_id"]')?.value;

                // Build URL if not provided
                const url = options.urlBuilder ?
                    options.urlBuilder(entityId) :
                    options.url;

                await this.submit(form, {
                    ...options,
                    entityId,
                    url
                });
            });
        }
    };

    // Expose globally
    window.TurboEditModal = TurboEditModal;
})();
